import { SelectOption, useFormNotification } from "@commonlibs";
import { useControlled } from "@commonlibs";
import { useState, useMemo, useEffect } from "react";
import useTranslate from "ui/locale/hooks/useTranslate";
import { createEmptyOptions, defaultFilterOption } from "./useAutocomplete.utils";
import { UseAutocompleteParams } from "./useAutocomplete.types";

export default function useAutocomplete<T>({
    defaultSearch = "",
    search: searchProp,
    onSearchChange,
    defaultValue,
    value,
    onChange,
    options,
    isLoading,
    filterOption = defaultFilterOption,
    name,
    required,
    loadingText,
    nothingToShow,
}: UseAutocompleteParams<T>) {
    const translate = useTranslate();
    const [search, setSearch] = useControlled(defaultSearch, searchProp, onSearchChange);
    const [selectedOption, setSelectedOption] = useState<SelectOption<T> | undefined>(undefined); // skipcq: JS-W1042
    const [hasSettedDefault, setHasSettedDefault] = useState(false);
    const [selected, setSelected] = useControlled(defaultValue as T, value, onChange);
    const [open, setOpened] = useState(false);

    /**
     * HANDLERS
     */
    function handleSearch(value: string) {
        if (!open) setOpened(true);
        setSearch(value);
    }

    function handleSelect(value: T) {
        if (isLoading) return;
        else if (value === "") setSearch("");
        setSelected(value);
        setTimeout(() => {
            const option = options?.find((o) => o.value === value);
            setSelectedOption(option);
            setSearch(String(option?.label || ""));
        });
    }

    function handleClose() {
        setOpened(false);
        setTimeout(() => {
            const searchOption = options?.find((o) => o.label === search);
            if (searchOption) {
                setSelected(searchOption.value);
                setSelectedOption(searchOption);
            }
        });
    }



    /**
     * EFFECTS
     */
    useEffect(() => {
        if (hasSettedDefault || search !== "") return;
        const option = options?.find((o) => o.value === defaultValue);
        if (option) {
            setSearch(String(option?.label || ""));
            setSelectedOption(option);
            setHasSettedDefault(true);
        }
    }, [defaultValue, options]);

    const candidates = useMemo(() => {
        if (isLoading) return createEmptyOptions(loadingText || translate("loading"));
        if (search === "") return options;
        const filteredCandidates = options?.filter((o) => filterOption(o, search)) || [];
        if (filteredCandidates.length === 0) {
            return createEmptyOptions(nothingToShow || translate("noResultsForThisSearch", { ns: "error" }));
        }
        return filteredCandidates;
    }, [search, options, isLoading]) as SelectOption<T>[];

    const isValid = !isLoading && (selected ? search !== "" && selectedOption?.label === search : !required);

    useFormNotification(name, selected, isValid);

    return {
        inputProps: { value: search, onChange: handleSearch },
        selectProps: {
            open,
            onClose: handleClose,
            onOpen: () => setOpened(true),
            onChange: handleSelect,
            value: selected,
            options: candidates,
        },
    };
}
